import { HTMLAttributes, ReactNode } from 'react';

/**
 * Badge — soft-tinted status pill. Matches DreamsPOS .badge .badge-soft-X.
 * Used for order statuses, stock chips, etc.
 */
type Tone = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'gray' | 'purple' | 'pink' | 'teal';

interface Props extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  variant?: 'soft' | 'solid' | 'outline';
  size?: 'sm' | 'md';
  children: ReactNode;
}

const SOFT: Record<Tone, string> = {
  primary:   'bg-primary-soft text-primary',
  secondary: 'bg-secondary-soft text-secondary',
  success:   'bg-success-soft text-success',
  danger:    'bg-danger-soft text-danger',
  warning:   'bg-warning-soft text-warning',
  info:      'bg-info-soft text-info',
  gray:      'bg-gray-100 text-gray-700',
  purple:    'bg-purple-soft text-purple',
  pink:      'bg-pink-soft text-pink',
  teal:      'bg-teal-soft text-teal',
};
const SOLID: Record<Tone, string> = {
  primary:   'bg-primary text-[#FFFFFF]',
  secondary: 'bg-secondary text-[#FFFFFF]',
  success:   'bg-success text-[#FFFFFF]',
  danger:    'bg-danger text-[#FFFFFF]',
  warning:   'bg-warning text-[#FFFFFF]',
  info:      'bg-info text-[#FFFFFF]',
  gray:      'bg-gray-700 text-[#FFFFFF]',
  purple:    'bg-purple text-[#FFFFFF]',
  pink:      'bg-pink text-[#FFFFFF]',
  teal:      'bg-teal text-[#FFFFFF]',
};
const OUTLINE: Record<Tone, string> = {
  primary:   'border border-primary text-primary',
  secondary: 'border border-secondary text-secondary',
  success:   'border border-success text-success',
  danger:    'border border-danger text-danger',
  warning:   'border border-warning text-warning',
  info:      'border border-info text-info',
  gray:      'border border-gray-300 text-gray-700',
  purple:    'border border-purple text-purple',
  pink:      'border border-pink text-pink',
  teal:      'border border-teal text-teal',
};

const SIZE = {
  sm: 'h-5 px-2 text-10',
  md: 'h-6 px-2.5 text-11',
};

export default function Badge({ tone = 'gray', variant = 'soft', size = 'md', className = '', children, ...rest }: Props) {
  const palette =
    variant === 'solid' ? SOLID[tone]
    : variant === 'outline' ? OUTLINE[tone]
    : SOFT[tone];

  return (
    <span
      className={[
        'inline-flex items-center justify-center rounded-md font-medium uppercase tracking-wide',
        SIZE[size],
        palette,
        className,
      ].join(' ')}
      {...rest}
    >
      {children}
    </span>
  );
}
